import { Badge, Button, cn } from "@devhop/ui";
import { FileText, Minus, Plus, Split, Trash2, X } from "lucide-react";
import { useEffect, useState } from "react";
import type { CartItem as CartItemType } from "../context/CartContext";
import { useCart } from "../context/CartContext";
import { NoteDialog } from "./NoteDialog";
import { SplitItemDialog } from "./SplitItemDialog";

type CartItemProps = {
  item: CartItemType;
};

export function CartItem({ item }: CartItemProps) {
  const {
    updateQuantity,
    removeItem,
    updateNote,
    splitItem,
    mergeItems,
    items,
    lastAddedFeedback,
  } = useCart();
  const [isEntering, setIsEntering] = useState(false);
  const [isNoteDialogOpen, setIsNoteDialogOpen] = useState(false);
  const [isSplitDialogOpen, setIsSplitDialogOpen] = useState(false);

  useEffect(() => {
    if (lastAddedFeedback?.itemId !== item.id) return;
    setIsEntering(true);
    const timer = window.setTimeout(() => setIsEntering(false), 800);
    return () => window.clearTimeout(timer);
  }, [lastAddedFeedback?.token, item.id]);

  const handleIncrease = () => {
    updateQuantity(item.id, item.quantity + 1);
  };

  const handleDecrease = () => {
    updateQuantity(item.id, item.quantity - 1);
  };

  const handleRemove = () => {
    removeItem(item.id);
  };

  const handleNoteSave = (note: string) => {
    updateNote(item.id, note);
  };

  const handleSplit = (splitQuantity: number) => {
    splitItem(item.id, splitQuantity);
  };

  const handleMerge = () => {
    // Find mergeable item (same variant, modifiers, options)
    const mergeableItem = items.find((otherItem) => {
      if (otherItem.id === item.id) return false;
      if (otherItem.variantId !== item.variantId) return false;
      if (
        JSON.stringify(otherItem.selectedOptions) !==
        JSON.stringify(item.selectedOptions)
      ) {
        return false;
      }
      if (otherItem.modifiers.length !== item.modifiers.length) return false;
      return item.modifiers.every((mod1) => {
        return otherItem.modifiers.some(
          (mod2) =>
            mod1.modifierId === mod2.modifierId &&
            mod1.optionValue === mod2.optionValue,
        );
      });
    });

    if (mergeableItem) {
      mergeItems(item.id, mergeableItem.id);
    }
  };

  const canMerge = items.some((otherItem) => {
    if (otherItem.id === item.id) return false;
    if (otherItem.variantId !== item.variantId) return false;
    if (
      JSON.stringify(otherItem.selectedOptions) !==
      JSON.stringify(item.selectedOptions)
    ) {
      return false;
    }
    if (otherItem.modifiers.length !== item.modifiers.length) return false;
    return item.modifiers.every((mod1) => {
      return otherItem.modifiers.some(
        (mod2) =>
          mod1.modifierId === mod2.modifierId &&
          mod1.optionValue === mod2.optionValue,
      );
    });
  });

  const unitPrice =
    item.price + item.modifiers.reduce((sum, mod) => sum + mod.priceDelta, 0);

  return (
    <>
      <div
        className={cn(
          "flex flex-col gap-2 rounded-lg border-b pb-3 last:border-b-0",
          isEntering && "animate-cart-item-enter",
        )}
      >
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <h4 className="font-medium text-sm">{item.productName}</h4>
              {item.note && (
                <Badge variant="secondary" className="text-xs">
                  <FileText className="mr-1 h-3 w-3" />
                  ມີໝາຍເຫດ
                </Badge>
              )}
            </div>
            {item.variantName && (
              <p className="text-muted-foreground text-xs">
                {item.variantName}
              </p>
            )}
            {item.note && (
              <p className="mt-1 text-muted-foreground text-xs italic">
                "{item.note}"
              </p>
            )}
            {item.modifiers.length > 0 && (
              <div className="mt-1 space-y-0.5">
                {item.modifiers.map((modifier, idx) => (
                  <p key={idx} className="text-muted-foreground text-xs">
                    + {modifier.modifierName}: {modifier.optionLabel}
                    {modifier.priceDelta !== 0 && (
                      <span className="ml-1">
                        ({modifier.priceDelta > 0 ? "+" : ""}
                        {modifier.priceDelta.toLocaleString("lo-LA")} ₭)
                      </span>
                    )}
                  </p>
                ))}
              </div>
            )}
          </div>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 shrink-0 text-destructive hover:text-destructive"
            onClick={handleRemove}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>

        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            className="h-7 text-xs"
            onClick={() => setIsNoteDialogOpen(true)}
          >
            <FileText className="mr-1 h-3 w-3" />
            {item.note ? "ແກ້ໄຂໝາຍເຫດ" : "ເພີ່ມໝາຍເຫດ"}
          </Button>
          {item.quantity > 1 && (
            <Button
              variant="ghost"
              size="sm"
              className="h-7 text-xs"
              onClick={() => setIsSplitDialogOpen(true)}
            >
              <Split className="mr-1 h-3 w-3" />
              ແບ່ງ
            </Button>
          )}
          {canMerge && (
            <Button
              variant="ghost"
              size="sm"
              className="h-7 text-xs"
              onClick={handleMerge}
            >
              <X className="mr-1 h-3 w-3 rotate-45" />
              ລວມ
            </Button>
          )}
        </div>

        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8"
              onClick={handleDecrease}
            >
              <Minus className="h-4 w-4" />
            </Button>
            <span className="w-8 text-center font-medium text-sm">
              {item.quantity}
            </span>
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8"
              onClick={handleIncrease}
            >
              <Plus className="h-4 w-4" />
            </Button>
          </div>
          <div className="text-right">
            <p className="font-semibold text-sm">
              {item.totalPrice.toLocaleString("lo-LA")} ₭
            </p>
            {item.quantity > 1 && (
              <p className="text-muted-foreground text-xs">
                {unitPrice.toLocaleString("lo-LA")} ₭ x {item.quantity}
              </p>
            )}
          </div>
        </div>
      </div>

      <NoteDialog
        open={isNoteDialogOpen}
        onOpenChange={setIsNoteDialogOpen}
        currentNote={item.note}
        onSave={handleNoteSave}
      />
      <SplitItemDialog
        open={isSplitDialogOpen}
        onOpenChange={setIsSplitDialogOpen}
        item={item}
        onConfirm={handleSplit}
      />
    </>
  );
}
